import resolveMasterUsageAltText from '../../../../store/helpers/mediaAlt/resolveMasterUsageAltText';
import { getMasterVariantIds } from '../../../../store/products/helpers/listingSyncHelpers';

import getSelectedMediaAssetIdsForChannel from './getSelectedMediaAssetIdsForChannel';
import resolveSyncAltAutomatically from './resolveSyncAltAutomatically';
import {
  getAllMasterVariantAssets,
  getProductMediaAssets,
} from './syncFlowMediaHelpers';

const findAssetById = (assetId, product, productsState) => {
  const assets = [
    ...getProductMediaAssets(product, productsState),
    ...getAllMasterVariantAssets(product, productsState),
  ];

  return assets.find((asset) => String(asset._id) === String(assetId)) || null;
};

const getScopedVariantIds = (masterVariantIds, product) => (
  getMasterVariantIds(product, masterVariantIds).map(String)
);

const collectMasterAltOptionsForAsset = (asset, {
  masterProductId,
  masterProductTitle,
  product,
  selectedInGallery,
  selectedVariantIds,
}) => {
  const options = [];

  if (selectedInGallery) {
    options.push({
      altText: resolveMasterUsageAltText(asset, { productId: masterProductId, isVariant: false }),
      sourceType: 'product',
      sourceLabel: masterProductTitle,
    });
  }

  selectedVariantIds.forEach((variantId) => {
    const variant = product?.variants?.find(({ _id }) => String(_id) === String(variantId));
    const variantLabel = variant?.displayName || variant?.title || variantId;

    options.push({
      altText: resolveMasterUsageAltText(asset, {
        productId: masterProductId,
        variantId,
        isVariant: true,
      }),
      sourceType: 'variant',
      sourceLabel: variantLabel,
    });
  });

  return options;
};

const getSelectedVariantIdsForAsset = ({
  assetId,
  channelId,
  variantMediaByChannel,
  scopedVariantIds,
}) => {
  const channelMap = variantMediaByChannel[channelId] || {};
  const variantIds = new Set();

  Object.entries(channelMap).forEach(([mappedAssetId, mappedVariantIds]) => {
    if (String(mappedAssetId) !== String(assetId)) return;

    (mappedVariantIds || []).forEach((variantId) => {
      const normalizedVariantId = String(variantId);
      if (!scopedVariantIds.length || scopedVariantIds.includes(normalizedVariantId)) {
        variantIds.add(normalizedVariantId);
      }
    });
  });

  return [...variantIds];
};

export const collectSyncAltConflictsForChannel = ({
  channelId,
  product,
  productsState,
  productMediaByChannel = {},
  variantMediaByChannel = {},
  masterVariantIds,
  isVariantSync = false,
}) => {
  const masterProductId = product?._id;
  const masterProductTitle = product?.title || 'Master Product';
  const scopedVariantIds = getScopedVariantIds(masterVariantIds, product);
  const selectedAssetIds = getSelectedMediaAssetIdsForChannel({
    channelId,
    productMediaByChannel,
    variantMediaByChannel,
    isVariantSync,
  });

  const conflicts = [];
  const autoResolved = {};

  selectedAssetIds.forEach((assetId) => {
    const asset = findAssetById(assetId, product, productsState);
    if (!asset) return;

    const selectedInGallery = !isVariantSync
      && (productMediaByChannel[channelId] || []).some((id) => String(id) === String(assetId));
    const selectedVariantIds = getSelectedVariantIdsForAsset({
      assetId,
      channelId,
      variantMediaByChannel,
      scopedVariantIds,
    });

    const options = collectMasterAltOptionsForAsset(asset, {
      masterProductId,
      masterProductTitle,
      product,
      selectedInGallery,
      selectedVariantIds,
    });

    const resolution = resolveSyncAltAutomatically(options);

    if (resolution.needsUserChoice) {
      conflicts.push({
        assetId: String(assetId),
        asset,
        options: resolution.options,
      });
      return;
    }

    autoResolved[String(assetId)] = resolution.resolved ?? '';
  });

  return { conflicts, autoResolved };
};

export const collectSyncAltConflictsByChannel = ({
  selectedChannels = [],
  ...params
}) => selectedChannels.reduce((acc, channelId) => {
  const { conflicts } = collectSyncAltConflictsForChannel({
    channelId,
    ...params,
  });

  if (conflicts.length) {
    acc[channelId] = conflicts;
  }

  return acc;
}, {});

export default collectSyncAltConflictsForChannel;
